<template>
  <ValidationProvider
    :name="label"
    :rules="rules"
    :vid="label"
    v-bind="validationAttrs"
    v-slot="{ classes, errors }"
    tag="div"
    ref="valProvider"
  >
    <div class="d-flex">
      <div class="label-wrapper flex-grow-1"
        aria-live="assertive"
      >
        <label :for="id" :class="[
          'form-label',
          { 'text-danger': !!errors.length }
        ]">
          {{ label }}<span v-if="rules.required" class="text-danger">*</span>
        </label>
        <transition name="slide" mode="out-in">
          <span :id="`error-msg-${id}`"
            :key="errors[0]"
            class="error-msg text-danger"
          ><span class="visually-hidden">{{ label }}</span> {{ errors[0] }}</span>
        </transition>
      </div>
      <PopOver v-if="helpTip || $slots.helpTip"
        class="me-1"
        placement="top"
        :title="label"
        :show-triggers="['click', 'hover']"
        :hide-triggers="['focus', 'hover']"
      >
        <template #reference>
          <IconButton
            iconClass="bi bi-question-circle"
            :title="`Show help tips for ${ label }`"
          />
        </template>

        <slot name="helpTip">{{ helpTip }}</slot>
      </PopOver>
    </div>

    <ReflectAttrs
      v-bind="{
        allControlAttrs: {
          ...controlAttrs,
          'aria-describedby': [
            errors.length ? `error-msg-${id}` : '',
            showDescription ? `description-${id}` : ''
          ].filter(x => x).join(' '),
          'aria-invalid': errors.length ? 'true' : 'false',
          'aria-required': rules.required ? 'true' : null
        },
        controlClasses: ['form-control', ...classes]
      }"
      v-slot="{ allControlAttrs, controlClasses }"
    >
      <slot
        :attrs="allControlAttrs"
        :classes="controlClasses"
        :listeners="controlListeners"
      ></slot>
    </ReflectAttrs>

    <div v-if="showDescription"
      :id="`description-${id}`"
    >
      <slot name="description">
        <small class="opacity-75 ps-1">{{ description }}</small>
      </slot>
    </div>
  </ValidationProvider>
</template>

<script>
import focusable from '@ssi/vue-common/src/mixins/focusable';
import { defaultSchema } from '@ssi/vue-common/src/directives/focus';
import PopOver from '@/components/PopOver';
import IconButton from '@/components/buttons/Icon';

const { prop: focusProp } = defaultSchema;

export default {
  inheritAttrs: false,

  mixins: [focusable],

  components: { IconButton, PopOver },

  props: {
    description: String,

    helpTip: String,

    label: {
      type: String,
      required: true
    },

    rules: {
      type: Object,
      default: () => ({})
    },

    /**
     * Attributes/props to pass to the VeeValidate ValidationProvider.
     */
    validationAttrs: {
      type: Object,
      default: () => ({})
    }
  },

  computed: {
    controlAttrs() {
      return {
        ...this.$attrs,
        // Can't use v-focus directive on a slot, so add explicit
        // prop to support it.
        [focusProp]: this[focusProp],
        id: this.id
      };
    },

    controlListeners() {
      return {
        ...this.$listeners,
        // Can't use v-focus directive on a slot, so add explicit
        // event handlers to support it.
        blur: () => { this.hasFocus = false; },
        focus: () => { this.hasFocus = true; },
        invalid: (e) => {
          setTimeout(() => {
            this.$refs.valProvider.setErrors([e.message]);
          }, 1);
        }
      };
    },

    id() { return this.uniqName(this.label.toLowerCase().replace(/\s/g, '-')); },

    showDescription() {
      return Boolean(this.description || this.$slots.description);
    }
  }
};
</script>

<style lang="scss" scoped>
.error-msg {
  font-size: 0.75em;
  font-weight: normal;
  line-height: 1em;
}

.label-wrapper {
  align-items: center;
  display: flex;
  gap: 0.5em;
  margin-bottom: 0.5rem;
}

.form-label {
  margin-bottom: 0;
  white-space: nowrap;
}

$transition-duration: 0.25s;

.form-label {
  transition: color $transition-duration;
}

$translate-distance: 0.8em;

.slide-enter-active, .slide-leave-active {
  transition-duration: $transition-duration;
  transition-property: opacity, transform;
}
.slide-enter, .slide-leave-to {
  opacity: 0;
}
.slide-enter {
  transform: translateY(-$translate-distance);
}
.slide-leave-to {
  transform: translateY($translate-distance);
}
</style>
